import React, { PropsWithChildren, useCallback, useState } from "react";
import { ColorValue } from "react-native";
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from "react-native-reanimated";
import { SafeAreaView, useSafeAreaInsets } from "react-native-safe-area-context";
import { useTheme } from "../hooks/use-theme";

interface SafeAreaContextInterface {
    setStatusBarTranslucent: (value: boolean) => void;
    setStatusBarColor: (color: ColorValue) => void;
    resetStatusBarColor: () => void;
}

export const SafeAreaManagerContext = React.createContext<SafeAreaContextInterface>({
    setStatusBarTranslucent: () => {},
    setStatusBarColor: () => {},
    resetStatusBarColor: () => {},
});

const SafeAreaManager = ({ children }: PropsWithChildren) => {
    const [statusBarTranslucent, setStatusBarTranslucent] = useState(true);
    const { colors } = useTheme();

    const initialColor = colors.background;

    const statusBarColor = useSharedValue<ColorValue>(initialColor);

    const setStatusBarColor = useCallback(
        (color: ColorValue) => {
            statusBarColor.value = withTiming(color as string);
        },
        [statusBarColor],
    );

    const resetStatusBarColor = useCallback(() => {
        statusBarColor.value = withTiming(initialColor);
    }, [statusBarColor, initialColor]);

    const animatedStatusBarStyles = useAnimatedStyle(() => ({
        backgroundColor: statusBarColor.value,
    }));
    const insets = useSafeAreaInsets();

    return (
        <SafeAreaManagerContext.Provider
            value={{
                setStatusBarTranslucent,
                setStatusBarColor,
                resetStatusBarColor,
            }}
        >
            {statusBarTranslucent ? (
                <Animated.View style={[{ height: insets.top }, animatedStatusBarStyles]} />
            ) : null}
            <SafeAreaView
                style={{ flex: 1 }}
                // we adjust the bottom edge, to avoid effect of floating tab bar
                edges={{
                    bottom: "off",
                    top: statusBarTranslucent ? "off" : undefined,
                    left: "additive",
                    right: "additive",
                }}
            >
                {children}
            </SafeAreaView>
        </SafeAreaManagerContext.Provider>
    );
};

export default SafeAreaManager;
